import { useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';
import type { Endpoint, Project } from '@mockd/shared';
import { api, ApiError } from '../utils/apiClient';
import { useProjects } from './useProjects';

const INSTANT_MOCK_ENDPOINT = {
  path: '/webhooks/incoming',
  statusCode: 200,
  responseBody: JSON.stringify({
    received: true,
    id: '{{$uuid}}',
    timestamp: '{{$timestamp}}',
    method: '{{request.method}}',
    you_sent: '{{request.body}}',
    processed_by: '{{$randomName}}',
  }, null, 2),
};

interface UseInstantMockOptions {
  onProjectCreated?: (project: Project) => void;
}

/**
 * The "Instant Mock" quick start: an anonymous project with one webhook
 * endpoint, then straight to that endpoint in demo mode.
 */
export function useInstantMock({ onProjectCreated }: UseInstantMockOptions = {}) {
  const navigate = useNavigate();
  const { createAnonymousProject } = useProjects();
  const [isCreating, setIsCreating] = useState(false);

  const createInstantMock = useCallback(async () => {
    setIsCreating(true);
    try {
      let project: Project;
      try {
        project = await createAnonymousProject('Webhook Receiver');
      } catch {
        // createAnonymousProject has already toasted the failure
        return;
      }
      onProjectCreated?.(project);

      try {
        const json = await api.post<{ data: Endpoint }>(
          `/api/projects/${project.id}/endpoints`,
          INSTANT_MOCK_ENDPOINT,
        );
        navigate(`/projects/${project.id}/endpoints/${json.data.id}?demo=true`);
      } catch (err) {
        const message = err instanceof ApiError ? err.message : 'Request failed';
        toast.error(`Project created, but the demo endpoint couldn't be added: ${message}`);
        navigate(`/projects/${project.id}`);
      }
    } finally {
      setIsCreating(false);
    }
  }, [createAnonymousProject, navigate, onProjectCreated]);

  return { createInstantMock, isCreating };
}
